/**
 * GovRAMP program rules — V103-3-2 / ADR-0064, the ADR-0058 pattern (D-102-17).
 *
 * Rule *values* for the `govramp` program are versioned, reviewed content in
 * the `govramp` rule-set family (migration 328, generated by
 * `scripts/content/gen-govramp-rules.mjs` from the V103-1-4 research,
 * `docs/v10.3/content-spike.md` §3). Nothing here hard-codes a value:
 *   - tier sets (which baseline tier each profile tier reads, with the count);
 *   - ConMon cadence and bundle per tier, and the annual resubmission /
 *     assessment;
 *   - POA&M remediation timeframes by risk, with the CISA KEV override;
 *   - Progressing Snapshot scoring (per-control MITRE protection value ÷ the
 *     matrix denominator, no partial credit), the must-improve rule with its
 *     exceptions, and the escalation steps.
 * Every value carries a `source` citation naming a key in `sources`. Values
 * the research could not verify are left absent and listed in `unverified`
 * (PD-3): the Critical timeframe, the Snapshot escalation day counts, the
 * Low lateness trigger.
 *
 * The helpers below are pure: they read a parsed rule set and never the DB.
 */
import { z } from 'zod';
import { addTimeframe } from './certification-service.js';
export const GOVRAMP_RULE_FAMILY = 'govramp';
const text = z.string().trim().min(1).max(500);
/** "[CONMON] §3.2(2) p.10; matrix RA-5(d)": at least one `[KEY]` that `sources` defines. */
const citation = z.string().trim().min(4).max(300).regex(/\[[A-Z][A-Z0-9]*\]/, 'a source citation names a [KEY] from sources');
const REQ_ID = /^nist-800-53:[a-z]{2}-\d{1,2}(\.\d{1,2})?$/;
const TIER = z.enum(['snapshot', 'core', 'ready', 'authorized', 'provisional']);
const BASELINE_TIER = z.enum(['snapshot', 'core', 'ready', 'authorized']);
const RISK = z.enum(['high', 'moderate', 'low']);
const BUNDLE_ITEM = /^[a-z][a-z0-9_]{2,63}$/;
const Source = z.object({
    key: z.string().regex(/^[A-Z][A-Z0-9]*$/),
    title: text,
    url: z.string().url().max(500),
    version: text,
}).strict();
const ConmonRule = z.object({
    cadence_months: z.number().int().min(1).max(12),
    bundle: z.array(z.string().regex(BUNDLE_ITEM)).min(1).max(20),
    optional: z.array(z.string().regex(BUNDLE_ITEM)).max(10).optional(),
    source: citation,
}).strict();
const AnnualRule = z.object({
    kind: z.enum(['resubmission', 'assessment', 'snapshot_score_expiry']),
    months: z.number().int().min(1).max(36),
    detail: text,
    source: citation,
}).strict();
const TierRule = z.object({
    tier: TIER,
    baseline_tier: BASELINE_TIER,
    requirement_count: z.number().int().min(1).max(1000),
    /** Null for Snapshot: no ConMon, a new Snapshot every quarter instead (`snapshot.cadence_months`). */
    conmon: ConmonRule.nullable(),
    annual: AnnualRule,
    source: citation,
}).strict();
const Timeframe = z.object({
    risk: RISK,
    days: z.number().int().min(1).max(730),
    /** `findings.severity` values that take this timeframe. */
    finding_severities: z.array(z.enum(['critical', 'high', 'medium', 'low'])).min(1).max(4),
    source: citation,
}).strict();
const LateTrigger = z.object({
    risk: RISK,
    detailed_finding_review: z.object({ min_items: z.number().int().min(1), older_than_days: z.number().int().min(1), or_inventory_percent: z.number().min(0).max(100) }).strict(),
    cap_older_than_days: z.number().int().min(1),
    source: citation,
}).strict();
const SnapshotControl = z.object({
    requirement_id: z.string().regex(REQ_ID),
    mitre_protection_value: z.number().positive().max(1000),
}).strict();
const ImprovementException = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('score_above_percent'), percent: z.number().min(0).max(100) }).strict(),
    z.object({ kind: z.literal('advisor_written_confirmation') }).strict(),
]);
const RemediationEvent = z.object({
    event: z.enum(['identical_score', 'score_reduction', 'artifact_age']),
    label: text,
    exceptions: z.array(ImprovementException).max(5),
    max_age_months: z.number().int().min(1).max(60).optional(),
    source: citation,
}).strict();
const EscalationStep = z.object({
    step: z.string().regex(BUNDLE_ITEM),
    label: text,
    /** Days after the previous step. UNVERIFIED for Snapshot (spike §4.2): left absent. */
    after_days: z.number().int().min(1).nullable().optional(),
}).strict();
export const GovrampRulesV1 = z.object({
    schema: z.literal('gacpro.program-rules.govramp/1'),
    program_key: z.literal('govramp'),
    baseline: z.object({ key: z.string().regex(/^[a-z0-9][a-z0-9-]*$/), impact_level: z.literal('moderate'), source_version: text, source: citation }).strict(),
    sources: z.array(Source).min(1).max(20),
    tiers: z.array(TierRule).min(1).max(10),
    conmon_escalation: z.object({ steps: z.array(EscalationStep).min(1).max(10), source: citation }).strict(),
    poam: z.object({
        timeframes: z.array(Timeframe).min(1).max(5),
        kev_due_date_overrides: z.literal(true),
        kev_source: citation,
        shorter_control_timeframes_apply: z.boolean(),
        shorter_source: citation,
        late_triggers: z.array(LateTrigger).max(5),
    }).strict(),
    snapshot: z.object({
        baseline_tier: z.literal('snapshot'),
        cadence_months: z.number().int().min(1).max(12),
        cadence_source: citation,
        scoring: z.object({
            method: z.literal('weighted_complete'),
            partial_credit: z.literal(false),
            denominator: z.number().positive(),
            controls: z.array(SnapshotControl).min(1).max(100),
            source: citation,
        }).strict(),
        listing_requires_nonzero_score: z.boolean(),
        listing_source: citation,
        evidence_max_age_months: z.number().int().min(1).max(60),
        score_valid_months: z.number().int().min(1).max(60),
        validity_source: citation,
        must_improve: z.object({
            rule: text,
            effective_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
            events: z.array(RemediationEvent).min(1).max(10),
            source: citation,
        }).strict(),
        escalation: z.object({ steps: z.array(EscalationStep).min(1).max(10), source: citation }).strict(),
    }).strict(),
    /** Values the research could not verify (PD-3); each is absent above. */
    unverified: z.array(text).max(20),
}).strict().superRefine((r, ctx) => {
    const issue = (message) => ctx.addIssue({ code: 'custom', message });
    const keys = new Set();
    for (const s of r.sources) {
        if (keys.has(s.key))
            issue(`duplicate source ${s.key}`);
        keys.add(s.key);
    }
    // Every citation names only defined sources.
    const walk = (v, path) => {
        if (Array.isArray(v))
            v.forEach((x, i) => walk(x, `${path}[${i}]`));
        else if (v && typeof v === 'object') {
            for (const [k, x] of Object.entries(v)) {
                if ((k === 'source' || k.endsWith('_source')) && typeof x === 'string') {
                    for (const m of x.matchAll(/\[([A-Z][A-Z0-9]*)\]/g))
                        if (!keys.has(m[1]))
                            issue(`${path}.${k} cites unknown source [${m[1]}]`);
                }
                else
                    walk(x, `${path}.${k}`);
            }
        }
    };
    walk(r, '$');
    const tiers = new Set();
    for (const t of r.tiers) {
        if (tiers.has(t.tier))
            issue(`duplicate tier ${t.tier}`);
        tiers.add(t.tier);
        if (t.tier !== 'provisional' && t.baseline_tier !== t.tier)
            issue(`tier ${t.tier} reads baseline tier ${t.baseline_tier}`);
    }
    for (const t of TIER.options)
        if (!tiers.has(t))
            issue(`tier ${t} has no rule`);
    const risks = new Set();
    const severities = new Set();
    for (const f of r.poam.timeframes) {
        if (risks.has(f.risk))
            issue(`duplicate POA&M timeframe ${f.risk}`);
        risks.add(f.risk);
        for (const s of f.finding_severities) {
            if (severities.has(s))
                issue(`finding severity ${s} maps to two timeframes`);
            severities.add(s);
        }
    }
    const ids = new Set();
    let sum = 0;
    for (const c of r.snapshot.scoring.controls) {
        if (ids.has(c.requirement_id))
            issue(`duplicate Snapshot control ${c.requirement_id}`);
        ids.add(c.requirement_id);
        sum += c.mitre_protection_value;
    }
    const snapTier = r.tiers.find((t) => t.tier === 'snapshot');
    if (snapTier && snapTier.requirement_count !== r.snapshot.scoring.controls.length) {
        issue(`tier snapshot lists ${snapTier.requirement_count} requirements but scoring has ${r.snapshot.scoring.controls.length} controls`);
    }
    // The matrix denominator is the sum of the 40 MITRE values (weights sum to 1).
    if (Math.abs(sum - r.snapshot.scoring.denominator) > 0.005)
        issue(`scoring.denominator ${r.snapshot.scoring.denominator} ≠ Σ MITRE values ${sum.toFixed(2)}`);
});
export class GovrampRulesError extends Error {
    constructor(message) {
        super(message);
        this.name = 'GovrampRulesError';
    }
}
// ---- pure helpers ----------------------------------------------------------------------
export function govrampTierRule(rules, tier) {
    const t = rules.tiers.find((x) => x.tier === tier);
    if (!t)
        throw new GovrampRulesError(`GovRAMP rules have no tier ${tier}.`);
    return t;
}
/** ConMon cadence in months for a tier; null when the tier has no ConMon (Snapshot). */
export function conmonCadenceMonths(rules, tier) {
    return govrampTierRule(rules, tier).conmon?.cadence_months ?? null;
}
/**
 * ConMon due dates inside a period: one per elapsed cadence from the period
 * start, up to and including the period end. Persistence and late state are
 * V103-3-5 (`conmon_cycles`); this is the schedule the rule set implies.
 */
export function conmonDueDates(rules, tier, periodStart, periodEnd) {
    const months = conmonCadenceMonths(rules, tier);
    return months ? cadenceDueDates(months, periodStart, periodEnd) : [];
}
/**
 * The cycle schedule a period implies for a tier (V103-3-5): ConMon at the
 * tier's cadence, or for Snapshot a new Snapshot every `snapshot.cadence_months`.
 */
export function govrampCycleSchedule(rules, tier, periodStart, periodEnd) {
    const conmon = conmonCadenceMonths(rules, tier);
    const kind = conmon ? 'conmon' : 'snapshot';
    const months = conmon ?? rules.snapshot.cadence_months;
    return { kind, cadence_months: months, due_dates: cadenceDueDates(months, periodStart, periodEnd) };
}
/** Due dates every `months` from the period start, up to and including the period end. */
export function cadenceDueDates(months, periodStart, periodEnd) {
    const start = new Date(`${periodStart.slice(0, 10)}T00:00:00.000Z`);
    const end = `${periodEnd.slice(0, 10)}`;
    const out = [];
    for (let k = 1; k <= 60; k++) {
        const due = addTimeframe(start, k * months, 'months').toISOString().slice(0, 10);
        if (due > end)
            break;
        out.push(due);
    }
    return out;
}
/** The POA&M timeframe a risk (or a finding severity) takes; throws when the rules define none (e.g. Critical). */
export function poamTimeframe(rules, input) {
    const byRisk = input.risk ? rules.poam.timeframes.find((t) => t.risk === input.risk) : undefined;
    if (input.risk && !byRisk)
        throw new GovrampRulesError(`GovRAMP rules define no remediation timeframe for risk ${input.risk}; record it as ${rules.poam.timeframes.map((t) => t.risk).join(', ')}.`);
    if (byRisk)
        return byRisk;
    const sev = input.findingSeverity;
    const bySeverity = sev ? rules.poam.timeframes.find((t) => t.finding_severities.includes(sev)) : undefined;
    if (!bySeverity) {
        throw new GovrampRulesError(sev
            ? `GovRAMP rules define no remediation timeframe for a ${sev} finding; record the risk as ${rules.poam.timeframes.map((t) => t.risk).join(', ')}.`
            : 'A GovRAMP POA&M item needs a risk level (its remediation timeframe depends on it).');
    }
    return bySeverity;
}
/**
 * Due date for a POA&M item opened at `openedAt`: opened + the risk's
 * timeframe; a CISA KEV due date supersedes it (matrix RA-5(d)).
 */
export function poamDueAt(rules, input) {
    const tf = poamTimeframe(rules, input);
    if (input.kevDueAt) {
        const kev = new Date(input.kevDueAt);
        if (Number.isNaN(kev.getTime()))
            throw new GovrampRulesError('kevDueAt is not a date.');
        if (kev.toISOString() <= input.openedAt)
            throw new GovrampRulesError('The CISA KEV due date must be after the item opens.');
        return { risk: tf.risk, days: tf.days, due_at: kev.toISOString(), basis: 'kev' };
    }
    return { risk: tf.risk, days: tf.days, due_at: addTimeframe(new Date(input.openedAt), tf.days, 'days').toISOString(), basis: 'timeframe' };
}
/**
 * The Progressing Snapshot score ([SMX] `6_Scoring Calculator`!C3): the sum
 * of the weights of controls whose every part is evidenced. No partial credit:
 * a control is in `met` only when the caller found all its objectives met.
 */
export function scoreSnapshot(rules, met) {
    const { denominator, controls } = rules.snapshot.scoring;
    let metSum = 0;
    const rows = controls.map((c) => {
        const isMet = met.has(c.requirement_id);
        if (isMet)
            metSum += c.mitre_protection_value;
        return { requirement_id: c.requirement_id, mitre_protection_value: c.mitre_protection_value, weight: c.mitre_protection_value / denominator, met: isMet };
    });
    // Sum the raw values, divide once: Σ(v/d) and (Σv)/d differ only in float noise.
    const fraction = metSum / denominator;
    return { fraction, percent: Math.round(fraction * 10_000) / 100, controls: rows, met: rows.filter((r) => r.met).length, total: rows.length };
}
/**
 * The must-improve rule ([SNAP] §2.5–2.6): a Snapshot that equals the last
 * submitted one is a remediation event unless an exception holds (score above
 * the content's percent, or the advisor's written confirmation); a reduction
 * is an event with whatever exceptions the content lists for it. No last
 * score → no warning. Pure: persistence of submitted scores is V103-3-5.
 */
export function checkSnapshotImprovement(rules, input) {
    const base = { draft_percent: input.draftPercent, last_submitted_percent: input.lastSubmittedPercent };
    if (input.lastSubmittedPercent == null)
        return { warning: false, event: null, excepted_by: null, ...base };
    const cents = (n) => Math.round(n * 100);
    const draft = cents(input.draftPercent);
    const last = cents(input.lastSubmittedPercent);
    const kind = draft === last ? 'identical_score' : draft < last ? 'score_reduction' : null;
    if (!kind)
        return { warning: false, event: null, excepted_by: null, ...base };
    const ev = rules.snapshot.must_improve.events.find((e) => e.event === kind);
    if (!ev)
        return { warning: false, event: null, excepted_by: null, ...base };
    for (const x of ev.exceptions) {
        if (x.kind === 'score_above_percent' && draft > cents(x.percent))
            return { warning: false, event: kind, excepted_by: x.kind, ...base };
        if (x.kind === 'advisor_written_confirmation' && input.advisorConfirmed)
            return { warning: false, event: kind, excepted_by: x.kind, ...base };
    }
    return { warning: true, event: kind, excepted_by: null, ...base };
}
//# sourceMappingURL=govramp-program-rules.js.map